/**
 * Dorëzim – Pranim i Asetit — `/assets/handover/new` dhe `/assets/handover/:id`.
 *
 * Procesverbali është AKT, jo formular. Prandaj:
 *
 *  · **Drafti nuk prek asgjë.** Ruajtja e mban dokumentin; caktimet krijohen
 *    vetëm te konfirmimi. Përndryshe një dokument i hapur e i braktisur do ta
 *    linte pajisjen të shënuar si të dorëzuar te dikush që s'e ka marrë kurrë.
 *  · **Konfirmimi është gjithçka ose asgjë.** Nëse qoftë edhe një pajisje është
 *    ndërkohë te dikush tjetër, serveri nuk shkruan asnjë caktim — punonjësi nuk
 *    guxon të nënshkruajë për tri pajisje e të marrë dy.
 *  · **Nënshkrimet janë të dyja të detyrueshme.** Një procesverbal i nënshkruar
 *    nga një anë e vetme nuk është procesverbal.
 *
 * Hapat numërohen sepse ky është dokument që plotësohet nga lart-poshtë me
 * dikë përballë, jo një ekran ku kërkohet fusha e duhur.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useHistory, useLocation, Link } from 'react-router-dom';
import {
  Box, Paper, Typography, Stack, Button, TextField, MenuItem, Grid, Alert,
  IconButton, Table, TableHead, TableBody, TableRow, TableCell, Divider,
  Autocomplete, Checkbox, FormControlLabel, CircularProgress, Avatar,
} from '@mui/material';
import ArrowBackIcon      from '@mui/icons-material/ArrowBack';
import SaveIcon           from '@mui/icons-material/Save';
import AddIcon            from '@mui/icons-material/Add';
import DeleteOutlineIcon  from '@mui/icons-material/DeleteOutline';
import DownloadIcon       from '@mui/icons-material/Download';
import CheckCircleIcon    from '@mui/icons-material/CheckCircle';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';

import { ReportPage } from '../report/ReportKit';
import { FORM_DENSITY, SHRINK } from './FormKit';
import StatusChip     from '../common/StatusChip';
import Toast          from '../actions/Toasts';
import usePermissions from '../../hooks/usePermissions';
import useConfirm     from '../../hooks/useConfirm';
import assetsService  from '../../services/assetsService';
import apiService     from '../../services/apiServices';
import normalizeWorker from '../../services/workerNormalizer';
import SignaturePad   from './SignaturePad';
import WorkerPicker, { workerName } from '../common/WorkerPicker';
import { printHandover } from '../../utils/handoverPrint';
import {
  CONDITIONS, conditionLabel, assetTitle, money, dateText, DEKLARATA, canAssign,
  isDamagedReturn,
} from '../../config/assets';

const todayISO = () => new Date().toISOString().slice(0, 10);
const nowHM    = () => new Date().toTimeString().slice(0, 5);

/**
 * Dy lloje dokumenti, një faqe. Ndryshimi nuk është vetëm etiketë: te dorëzimi
 * lista mban pajisjet e lira, te kthimi vetëm ato që i mban VETË punonjësi —
 * dhe pikërisht ai kufizim e bën të pamundur mbylljen e caktimit të dikujt tjetër.
 */
const MODES = {
  handover: {
    title: 'Dorëzim – Pranim i Asetit',
    subtitle: 'Regjistro dorëzimin e aset(eve) te punonjësi',
    workerStep: 'Punonjësi pranues',
    itemsStep: 'Asetet e dorëzuara',
    conditionLabel: 'Gjendja në dorëzim',
    dateLabel: 'Data e dorëzimit',
    confirmLabel: 'Konfirmo & Gjenero PDF',
    giverLabel: 'Dorëzoi (përfaqësuesi i kompanisë)',
    receiverLabel: 'Pranoi (punonjësi)',
    emptyHint: 'Shto së paku një aset. Në listë dalin vetëm ato që dorëzohen dot — jo ato te dikush tjetër, në servis ose të humbura.',
    done: 'Procesverbali u konfirmua dhe asetet u dorëzuan.',
  },
  return: {
    title: 'Kthim – Pranim i Asetit',
    subtitle: 'Regjistro kthimin e aset(eve) nga punonjësi',
    workerStep: 'Punonjësi dorëzues',
    itemsStep: 'Asetet e kthyera',
    conditionLabel: 'Gjendja në kthim',
    dateLabel: 'Data e kthimit',
    confirmLabel: 'Konfirmo kthimin & Gjenero PDF',
    giverLabel: 'Dorëzoi (punonjësi)',
    receiverLabel: 'Pranoi (përfaqësuesi i kompanisë)',
    emptyHint: 'Zgjidh punonjësin — pastaj në listë dalin vetëm pajisjet që i mban ai.',
    done: 'Kthimi u konfirmua dhe pajisjet u liruan.',
  },
};

/** Nga një aset i regjistrit → një rresht i procesverbalit. */
const toItem = (a) => ({
  assetId: a.id, assetCode: a.assetCode, assetName: a.name,
  serialNumber: a.serialNumber, condition: a.condition || 'good',
  accessories: '', value: a.purchaseValue, note: '',
  damageCost: '', damageNote: '', createDeduction: false,
});

function Step({ n, title, children, action }) {
  return (
    <Paper variant="outlined" sx={{ borderRadius: 2, p: 1.5, minWidth: 0 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between"
        sx={{ mb: 1.25, minHeight: 26 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Box sx={{ width: 20, height: 20, borderRadius: '50%', bgcolor: 'primary.main', color: '#fff',
                     display: 'flex', alignItems: 'center', justifyContent: 'center',
                     fontSize: '0.66rem', fontWeight: 800 }}>
            {n}
          </Box>
          <Typography sx={{ fontWeight: 700, fontSize: '0.8rem', color: 'primary.main' }}>{title}</Typography>
        </Stack>
        {action}
      </Stack>
      {children}
    </Paper>
  );
}

export default function AssetHandoverPage() {
  const { id }   = useParams();
  const history  = useHistory();
  const location = useLocation();
  const editing  = !!id && id !== 'new';

  // Lloji vjen nga URL-ja te dokumenti i ri dhe nga vetë dokumenti te ai i ruajtur.
  const [kind, setKind] = useState(
    () => (new URLSearchParams(location.search).get('kind') === 'return' ? 'return' : 'handover'),
  );
  const M = MODES[kind];

  const [doc, setDoc]         = useState(null);
  const [workerId, setWorkerId] = useState('');
  const [items, setItems]     = useState([]);
  const [form, setForm]       = useState({
    docNo: '', happenedOn: todayISO(), happenedAt: nowHM(), location: '',
    departmentId: '', comment: '', declarationAccepted: false,
    giverName: '', giverSignature: '', receiverSignature: '',
  });

  const [assets, setAssets]           = useState([]);
  const [workers, setWorkers]         = useState([]);
  const [departments, setDepartments] = useState([]);
  const [picker, setPicker]           = useState(null);

  const [loading, setLoading] = useState(true);
  const [busy, setBusy]       = useState(false);
  const [error, setError]     = useState('');
  const [snack, setSnack]     = useState({ open: false, msg: '', sev: 'success' });
  const say = (msg, sev = 'success') => setSnack({ open: true, msg, sev });

  const { can } = usePermissions();
  const canManage = can('assets.manage');
  const [confirm, confirmUI] = useConfirm();

  const confirmed = doc?.status === 'confirmed';

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const [aList, wRes, dRes] = await Promise.all([
        assetsService.list(),
        apiService().get('workers'),
        apiService().get('departments'),
      ]);
      const arr = (r) => { const raw = r?.data || r; return Array.isArray(raw) ? raw : []; };
      const allWorkers = arr(wRes).map((w, i) => normalizeWorker(w, i));
      setAssets(Array.isArray(aList) ? aList : []);
      setWorkers(allWorkers);
      setDepartments(arr(dRes));

      if (editing) {
        const h = await assetsService.handover(id);
        setDoc(h);
        setKind(h.kind === 'return' ? 'return' : 'handover');
        setForm({
          docNo: h.docNo, happenedOn: h.happenedOn || todayISO(), happenedAt: h.happenedAt || '',
          location: h.location || '', departmentId: h.departmentId || '',
          comment: h.comment || '', declarationAccepted: h.declarationAccepted,
          giverName: h.giverName || '', giverSignature: h.giverSignature || '',
          receiverSignature: h.receiverSignature || '',
        });
        setItems(h.items || []);
        setWorkerId(h.workerId ?? '');
      } else {
        // `?asset=12` — hyrje nga faqja e një aseti: ai aset është arsyeja pse
        // përdoruesi erdhi këtu, ndaj rri i shtuar që në hapje.
        const pre = new URLSearchParams(location.search).get('asset');
        const one = pre && (Array.isArray(aList) ? aList : []).find((a) => String(a.id) === String(pre));
        if (one) {
          setItems([toItem(one)]);
          // Te kthimi punonjësi nuk pyetet: aseti e di vetë se kush e mban.
          if (one.holder?.workerId) setWorkerId(one.holder.workerId);
        }
      }
    } catch (e) {
      setError(e.message || 'Të dhënat nuk u ngarkuan.');
    } finally {
      setLoading(false);
    }
  }, [editing, id, location.search]);

  useEffect(() => { load(); }, [load]);

  /** Punonjësi i zgjedhur si objekt — emri, pozita dhe emaili shkojnë te dokumenti. */
  const worker = useMemo(
    () => workers.find((w) => String(w.Id) === String(workerId)) || null,
    [workers, workerId],
  );

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const usedIds = useMemo(() => new Set(items.map((x) => String(x.assetId))), [items]);

  // Te dorëzimi: vetëm ato që dorëzohen dot — një pajisje te dikush tjetër, e
  // humbur ose në servis do ta rrëzonte konfirmimin, dhe atëherë s'do të kishte
  // ku të hiqej. Te kthimi: vetëm ato që i mban VETË ky punonjës, ndaj një
  // caktim i dikujt tjetër nuk mbyllet dot as gabimisht.
  const available = useMemo(() => assets.filter((a) => {
    if (usedIds.has(String(a.id))) return false;
    return kind === 'return'
      ? String(a.holder?.workerId ?? '') === String(workerId)
      : canAssign(a);
  }), [assets, usedIds, kind, workerId]);

  const total = useMemo(
    () => Math.round(items.reduce((s, i) => s + (Number(i.value) || 0), 0) * 100) / 100,
    [items],
  );

  const ready = !!workerId && items.length > 0 && form.declarationAccepted
    && !!form.giverSignature && !!form.receiverSignature;

  // Te kthimi, ndërrimi i punonjësit i bën rreshtat e mëparshëm të pavlefshëm:
  // ato pajisje i mban dikush tjetër. Hiqen, në vend që konfirmimi të dështojë.
  useEffect(() => {
    if (kind !== 'return' || !workerId) return;
    setItems((rows) => rows.filter((r) => {
      const a = assets.find((x) => String(x.id) === String(r.assetId));
      return !a || String(a.holder?.workerId ?? '') === String(workerId);
    }));
  }, [kind, workerId, assets]);

  function updateItem(i, key, value) {
    setItems((rows) => rows.map((r, idx) => (idx === i ? { ...r, [key]: value } : r)));
  }

  function payload() {
    return {
      kind,
      workerId: workerId || null,
      departmentId: form.departmentId || null,
      location: form.location,
      happenedOn: form.happenedOn,
      happenedAt: form.happenedAt,
      comment: form.comment,
      declarationAccepted: form.declarationAccepted,
      giverName: form.giverName,
      giverSignature: form.giverSignature,
      receiverSignature: form.receiverSignature,
      items: items.map((i) => ({
        assetId: i.assetId, condition: i.condition,
        accessories: i.accessories, value: i.value, note: i.note,
        damageCost: i.damageCost === '' || i.damageCost === null ? null : Number(i.damageCost),
        damageNote: i.damageNote, createDeduction: !!i.createDeduction,
      })),
    };
  }

  async function saveDraft() {
    setBusy(true); setError('');
    try {
      const saved = editing
        ? await assetsService.updateHandover(id, payload())
        : await assetsService.createHandover(payload());
      setDoc(saved);
      setForm((f) => ({ ...f, docNo: saved.docNo }));
      say(`Procesverbali u ruajt si draft (${saved.docNo}).`);
      if (!editing) history.replace(`/assets/handover/${saved.id}`);
    } catch (e) {
      setError(e.message || 'Ruajtja dështoi.');
    } finally {
      setBusy(false);
    }
  }

  async function confirmDoc() {
    const okToGo = await confirm({
      title: 'Konfirmo procesverbalin',
      message: `${items.length} aset(e) i dorëzohen punonjësit ${worker ? workerName(worker) : ''}.`,
      details: 'Pas konfirmimit dokumenti nuk redaktohet më dhe caktimet hyjnë në fuqi.',
      confirmLabel: 'Konfirmo',
      color: 'primary',
    });
    if (!okToGo) return;
    setBusy(true); setError('');
    try {
      // Ruaj së pari: konfirmimi lexon atë që është në bazë, jo atë që rri te
      // ekrani — përndryshe një ndryshim i pambajtur do të humbte pa u vënë re.
      const saved = editing
        ? await assetsService.updateHandover(id, payload())
        : await assetsService.createHandover(payload());
      const done = await assetsService.confirmHandover(saved.id);
      setDoc(done);
      setItems(done.items || []);
      say(M.done);
      history.replace(`/assets/handover/${done.id}`);
    } catch (e) {
      setError(e.message || 'Konfirmimi dështoi.');
    } finally {
      setBusy(false);
    }
  }

  function doPrint() {
    const view = {
      ...(doc || {}),
      ...form,
      workerName: worker ? workerName(worker) : (doc?.workerName || ''),
      workerPosition: worker?.Position || doc?.workerPosition || '',
      workerEmail: worker?.Email || doc?.workerEmail || '',
      departmentName: departments.find((d) => String(d.Id ?? d.id) === String(form.departmentId))?.Name || doc?.departmentName || '',
      items,
      kind,
      status: doc?.status || 'draft',
      docNo: form.docNo || doc?.docNo || 'DRAFT',
    };
    if (!printHandover(view, { conditionLabel })) {
      say('Shfletuesi e bllokoi dritaren e printimit. Lejo pop-up-et për këtë faqe.', 'warning');
    }
  }

  if (loading) {
    return <ReportPage><Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box></ReportPage>;
  }

  return (
    <ReportPage sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, ...FORM_DENSITY }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" useFlexGap gap={1}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <IconButton size="small" onClick={() => history.push('/assets')} aria-label="Kthehu">
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.2 }}>{M.title}</Typography>
              {doc && (
                <StatusChip status={confirmed ? 'approved' : 'draft'}
                  label={confirmed ? 'I konfirmuar' : 'Draft'} size="sm" />
              )}
            </Stack>
            <Typography variant="caption" color="text.secondary">{M.subtitle}</Typography>
          </Box>
        </Stack>
        <Stack direction="row" spacing={1}>
          <Button size="small" variant="outlined" startIcon={<DownloadIcon />} onClick={doPrint}
            sx={{ textTransform: 'none' }}>
            Shkarko PDF
          </Button>
          {!confirmed && canManage && (
            <>
              <Button size="small" variant="outlined" startIcon={<SaveIcon />} onClick={saveDraft} disabled={busy}
                sx={{ textTransform: 'none' }}>
                Ruaj si draft
              </Button>
              <Button size="small" variant="contained" startIcon={<CheckCircleIcon />}
                onClick={confirmDoc} disabled={busy || !ready} sx={{ textTransform: 'none', fontWeight: 700 }}>
                {M.confirmLabel}
              </Button>
            </>
          )}
        </Stack>
      </Stack>

      {!!error && <Alert severity="error">{error}</Alert>}
      {confirmed && (
        <Alert severity="success">
          Ky procesverbal është konfirmuar më {dateText(doc.confirmedAt)} dhe nuk redaktohet më.
          Caktimet janë në fuqi.
        </Alert>
      )}

      <Grid container spacing={1.25} alignItems="flex-start">
        <Grid item xs={12} lg={8}>
          <Stack spacing={1.25}>
            <Step n={1} title="Informacioni i dorëzimit">
              <Grid container spacing={1.25}>
                <Grid item xs={12} sm={3}>
                  <TextField label="Nr. i procesverbalit" fullWidth size="small" disabled
                    value={form.docNo || 'Gjenerohet automatikisht'} {...SHRINK} />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <TextField type="date" label={M.dateLabel} fullWidth size="small" disabled={confirmed}
                    {...SHRINK} value={form.happenedOn} onChange={set('happenedOn')} />
                </Grid>
                <Grid item xs={6} sm={2}>
                  <TextField type="time" label="Ora" fullWidth size="small" disabled={confirmed}
                    {...SHRINK} value={form.happenedAt} onChange={set('happenedAt')} />
                </Grid>
                <Grid item xs={12} sm={2}>
                  <TextField label="Dega / Lokacioni" fullWidth size="small" disabled={confirmed}
                    value={form.location} onChange={set('location')} {...SHRINK} />
                </Grid>
                <Grid item xs={12} sm={2}>
                  <TextField select label="Departamenti" fullWidth size="small" disabled={confirmed}
                    value={form.departmentId} onChange={set('departmentId')} {...SHRINK}>
                    <MenuItem value="">—</MenuItem>
                    {departments.map((d) => (
                      <MenuItem key={d.Id ?? d.id} value={d.Id ?? d.id}>{d.Name || d.name}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
              </Grid>
            </Step>

            <Step n={2} title={M.workerStep}>
              <Grid container spacing={1.25}>
                <Grid item xs={12} sm={5}>
                  <WorkerPicker workers={workers} value={workerId} onChange={setWorkerId}
                    label="Punonjësi" required disabled={confirmed} />
                </Grid>
                <Grid item xs={12} sm={7}>
                  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ height: '100%' }}>
                    {worker ? (
                      <>
                        <Avatar sx={{ width: 34, height: 34 }}>{(worker.FirstName || '?').slice(0, 1)}</Avatar>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography sx={{ fontWeight: 700, fontSize: '0.85rem' }}>
                            {workerName(worker)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {[worker.Position, worker.Email, worker.Phone].filter(Boolean).join(' · ')}
                          </Typography>
                        </Box>
                      </>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Zgjidh punonjësin — të dhënat e tij mbushen vetvetiu.
                      </Typography>
                    )}
                  </Stack>
                </Grid>
              </Grid>
            </Step>

            <Step n={3} title={`${M.itemsStep} (${items.length})`}
              action={!confirmed && (
                <Autocomplete
                  size="small"
                  sx={{ width: 260 }}
                  options={available}
                  value={picker}
                  onChange={(_, v) => { if (v) { setItems((r) => [...r, toItem(v)]); setPicker(null); } }}
                  getOptionLabel={(a) => `${a.assetCode} · ${assetTitle(a)}`}
                  isOptionEqualToValue={(a, b) => a.id === b.id}
                  renderInput={(p) => <TextField {...p} label="Shto aset" size="small" />}
                />
              )}>
              {items.length === 0 ? (
                <Alert severity="info" icon={<AddIcon />}>{M.emptyHint}</Alert>
              ) : (
                <Box sx={{ overflowX: 'auto' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ width: 28, fontWeight: 700 }}>#</TableCell>
                        <TableCell sx={{ fontWeight: 700, minWidth: 180 }}>Aseti</TableCell>
                        <TableCell sx={{ fontWeight: 700, minWidth: 130 }}>Serial / ID</TableCell>
                        <TableCell sx={{ fontWeight: 700, minWidth: 140 }}>{M.conditionLabel}</TableCell>
                        <TableCell sx={{ fontWeight: 700, minWidth: 180 }}>Aksesorët</TableCell>
                        {kind === 'return' && (
                          <TableCell sx={{ fontWeight: 700, minWidth: 190 }}>Dëmi / ndalesa</TableCell>
                        )}
                        <TableCell className="num" sx={{ fontWeight: 700, minWidth: 110 }}>Vlera</TableCell>
                        {!confirmed && <TableCell />}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {items.map((it, i) => (
                        <TableRow key={it.assetId}>
                          <TableCell>{i + 1}</TableCell>
                          <TableCell>
                            <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600 }}>{it.assetName}</Typography>
                            <Typography variant="caption" color="text.secondary">{it.assetCode}</Typography>
                          </TableCell>
                          <TableCell sx={{ fontVariantNumeric: 'tabular-nums' }}>{it.serialNumber || '—'}</TableCell>
                          <TableCell>
                            {confirmed ? conditionLabel(it.condition) : (
                              <TextField select size="small" fullWidth value={it.condition}
                                onChange={(e) => updateItem(i, 'condition', e.target.value)}>
                                {CONDITIONS.map((c) => <MenuItem key={c.key} value={c.key}>{c.label}</MenuItem>)}
                              </TextField>
                            )}
                          </TableCell>
                          <TableCell>
                            {confirmed ? (it.accessories || '—') : (
                              <TextField size="small" fullWidth value={it.accessories || ''}
                                placeholder="Ngarkues, çantë…"
                                onChange={(e) => updateItem(i, 'accessories', e.target.value)} />
                            )}
                          </TableCell>
                          {/* Dëmi zihet KËTU, para nënshkrimit — ai që nënshkruan
                              duhet ta shohë shifrën për të cilën po nënshkruan.
                              Ndalesa është për rresht, jo për dokument: nga tri
                              pajisje, vetëm njëra mund të jetë e dëmtuar. */}
                          {kind === 'return' && (
                            <TableCell>
                              {!isDamagedReturn(it.condition) ? (
                                <Typography variant="body2" color="text.disabled">—</Typography>
                              ) : confirmed ? (
                                <Box>
                                  <Typography variant="body2">{money(it.damageCost)}</Typography>
                                  {it.deductionId ? (
                                    <Typography variant="caption" color="text.secondary">
                                      ndalesë #{it.deductionId}
                                    </Typography>
                                  ) : null}
                                </Box>
                              ) : (
                                <Stack spacing={0.5}>
                                  <TextField size="small" type="number" label="Kosto" value={it.damageCost ?? ''}
                                    onChange={(e) => updateItem(i, 'damageCost', e.target.value)} />
                                  <TextField size="small" label="Dëmtimi" value={it.damageNote || ''}
                                    onChange={(e) => updateItem(i, 'damageNote', e.target.value)} />
                                  <FormControlLabel
                                    sx={{ ml: 0, mt: -0.25 }}
                                    control={
                                      <Checkbox size="small" checked={!!it.createDeduction}
                                        disabled={!(Number(it.damageCost) > 0)}
                                        onChange={(e) => updateItem(i, 'createDeduction', e.target.checked)} />
                                    }
                                    label={<Typography variant="caption">Ndalesë nga paga</Typography>}
                                  />
                                </Stack>
                              )}
                            </TableCell>
                          )}
                          <TableCell className="num">{money(it.value)}</TableCell>
                          {!confirmed && (
                            <TableCell align="right">
                              <IconButton size="small" color="error"
                                onClick={() => setItems((r) => r.filter((_, idx) => idx !== i))}>
                                <DeleteOutlineIcon sx={{ fontSize: 17 }} />
                              </IconButton>
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  <Stack direction="row" justifyContent="flex-end" sx={{ mt: 1 }}>
                    <Typography sx={{ fontWeight: 800, fontSize: '0.85rem' }}>
                      Totali i vlerës (pa TVSH): {money(total)}
                    </Typography>
                  </Stack>
                </Box>
              )}
            </Step>

            <Step n={4} title="Komente">
              <TextField fullWidth size="small" multiline minRows={2} disabled={confirmed}
                value={form.comment} onChange={set('comment')}
                placeholder="Asetet janë dorëzuar në gjendjen e mësipërme dhe janë funksionale." {...SHRINK} />
            </Step>

            <Step n={5} title="Deklarata e përgjegjësisë">
              <Alert severity="info" icon={false} sx={{ mb: 1 }}>
                <Typography variant="body2">{kind === 'return' ? DEKLARATA.ret : DEKLARATA.handover}</Typography>
              </Alert>
              <FormControlLabel
                control={
                  <Checkbox checked={form.declarationAccepted} disabled={confirmed}
                    onChange={(e) => setForm((f) => ({ ...f, declarationAccepted: e.target.checked }))} />
                }
                label={<Typography variant="body2">Kam lexuar dhe pajtohem me deklaratën e mësipërme</Typography>}
              />
            </Step>

            <Step n={6} title="Nënshkrimet">
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField label={M.giverLabel} fullWidth size="small" disabled={confirmed}
                    value={form.giverName} onChange={set('giverName')} sx={{ mb: 1 }} {...SHRINK} />
                  <SignaturePad label="Nënshkrimi i dorëzuesit" value={form.giverSignature} disabled={confirmed}
                    onChange={(v) => setForm((f) => ({ ...f, giverSignature: v }))} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField label={M.receiverLabel} fullWidth size="small" disabled
                    value={worker ? workerName(worker) : ''}
                    sx={{ mb: 1 }} {...SHRINK} />
                  <SignaturePad label="Nënshkrimi i palës tjetër" value={form.receiverSignature} disabled={confirmed}
                    onChange={(v) => setForm((f) => ({ ...f, receiverSignature: v }))} />
                </Grid>
              </Grid>
            </Step>
          </Stack>
        </Grid>

        {/* ── Përmbledhja ── */}
        <Grid item xs={12} lg={4}>
          <Paper variant="outlined" sx={{ borderRadius: 2, p: 1.75, position: 'sticky', top: 12 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.25 }}>
              <Stack direction="row" alignItems="center" spacing={0.75}>
                <DescriptionOutlinedIcon sx={{ fontSize: 17, color: 'primary.main' }} />
                <Typography sx={{ fontWeight: 800, fontSize: '0.88rem' }}>
                  {kind === 'return' ? 'Përmbledhje e kthimit' : 'Përmbledhje e dorëzimit'}
                </Typography>
              </Stack>
              <StatusChip status={ready ? 'approved' : 'pending'}
                label={ready ? 'Gati për konfirmim' : 'I paplotë'} size="sm" />
            </Stack>

            <Stack spacing={0.35} sx={{ mb: 1.5 }}>
              {[
                ['Nr. i procesverbalit', form.docNo || '—'],
                ['Data', `${dateText(form.happenedOn)}${form.happenedAt ? `, ${form.happenedAt}` : ''}`],
                ['Punonjësi', worker ? workerName(worker) : '—'],
                ['Departamenti', departments.find((d) => String(d.Id ?? d.id) === String(form.departmentId))?.Name || '—'],
                ['Nr. i aseteve', String(items.length)],
                ['Totali i vlerës', money(total)],
              ].map(([k, v]) => (
                <Stack key={k} direction="row" justifyContent="space-between" sx={{ py: 0.3 }}>
                  <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>{k}</Typography>
                  <Typography sx={{ fontSize: '0.8rem', fontWeight: 700 }}>{v}</Typography>
                </Stack>
              ))}
            </Stack>

            {/* Ç'mungon thuhet me emër, jo si një buton i çaktivizuar pa shpjegim. */}
            {!ready && !confirmed && (
              <Alert severity="warning" sx={{ mb: 1.5 }}>
                Për konfirmim mungon:{' '}
                {[
                  !workerId && 'punonjësi',
                  !items.length && 'së paku një aset',
                  !form.declarationAccepted && 'pranimi i deklaratës',
                  !form.giverSignature && 'nënshkrimi i dorëzuesit',
                  !form.receiverSignature && 'nënshkrimi i punonjësit',
                ].filter(Boolean).join(', ')}.
              </Alert>
            )}

            <Divider sx={{ mb: 1.25 }} />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Drafti nuk prek asnjë aset. Caktimet krijohen vetëm te konfirmimi — dhe të gjitha
              njëherësh, ose asnjëra.
            </Typography>

            <Stack direction="row" spacing={1}>
              <Button fullWidth size="small" variant="outlined" startIcon={<DownloadIcon />} onClick={doPrint}
                sx={{ textTransform: 'none' }}>
                Shiko PDF
              </Button>
              {confirmed && (
                <Button fullWidth size="small" variant="contained" component={Link} to="/assets"
                  sx={{ textTransform: 'none', fontWeight: 700 }}>
                  Te regjistri
                </Button>
              )}
            </Stack>
          </Paper>
        </Grid>
      </Grid>

      {confirmUI}
      <Toast open={snack.open} message={snack.msg} severity={snack.sev}
        onClose={() => setSnack((s) => ({ ...s, open: false }))} />
    </ReportPage>
  );
}
